/**
 * 7-Year Life Clock — deterministic personal cycle math.
 *
 * Ported from the reference 7YearCycles project (js/index.js) so the
 * Growth Astrology reading can show the same "Personal Cycle Insight"
 * card. Pure functions, native Date only: no network calls, no AI tokens,
 * no external dependencies.
 *
 * Model in one line: a 7-year (84-month) cycle made of 12 seven-month
 * Zodiac Phases, each split into 4 Challenge Stages — 48 turning points
 * in total, within which the nearest one governs the present intensity.
 */

export const ZODIAC_PHASES = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

export const CHALLENGE_STAGES = [
  "Beginning Challenge Period",
  "2nd Quarter Challenge Period",
  "Mid Challenge Period",
  "4th Quarter Challenge Period",
];

// Cardinal phases (every 3rd phase) spark a growth process; fixed phases
// ask for internal revaluation. Indices are 0-based over 48 turning points.
const INITIATION_POINT_INDICES = new Set([0, 12, 24, 36]);
const REORIENTATION_POINT_INDICES = new Set([6, 18, 30, 42]);

// Shared challenge-window bounds (single source of truth for the card).
export const ACTIVE_CHALLENGE_START_DAYS = -16; // build-up begins 16 days before nearest TP
export const ACTIVE_CHALLENGE_END_DAYS = 3;     // Active and Cresting share the same end (+3)
export const CRESTING_START_DAYS = -3;
export const CRESTING_END_DAYS = 3;
const INITIATION_WINDOW_DAYS = 31;
const REORIENTATION_WINDOW_DAYS = 31;

/** Visual half-window (days either side of a pinnacle) for the wave graph. */
export const WAVE_WINDOW_DAYS = 22;

/**
 * The day, counted from a turning point's date, on which the focus hands over
 * to the next turning-point period: the focus stays on a turning point through
 * day +7 after its date and switches on day +8. The wave window itself stays
 * ±22 days, so after the handover the marker is simply absent from the new wave
 * until that next date comes within 22 days.
 */
export const WAVE_FOCUS_AFTER_DAYS = 8;

const INITIATION_WINDOW_GUIDANCE =
  "You are in the period of an Initiation Point. The influence of this period can extend to over 3 weeks before and after the Turning Point Date. You should take a higher elevation view of the overarching issue(s) that are coming to the forefront or are clearly in view to understand what challenge(s) the next period of time could initiate for you. Remember the areas subject to challenge and opportunity for growth can be personal, public, professional and can bring up anxiety, fear, sorrow, anger. The challenge(s) do not generally occur outside the context of your present life circumstances, but can be. They will work themselves out to some degree over the course of the 7 Month Phase.";

const REORIENTATION_WINDOW_GUIDANCE =
  "You are in the period of a Reorientation Point. These points are at the midpoint of the 7 Month Phases listed below. The influence of this period can extend to 3 weeks before and after the Turning Point Date. You should take a higher level view of the present challenge(s) related to issues of self-valuation, which is the focus of Reorientation Points. The self-valuation challenge can be in the context personal valuation (Taurus Phase), relational valuation (Leo Phase), public valuation (Scorpio Phase), and social valuation (Aquarius Phase).";

const LOW_CHALLENGE_GUIDANCE =
  "This period will provide a period of relative less challenge for you in which your experience of life feels more \"free flowing\".";

// ---------------------------------------------------------------------------
// Date helpers (native Date; month arithmetic clamps like moment does)
// ---------------------------------------------------------------------------

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return startOfDay(d);
}

/** Add whole months, clamping the day to the target month's length. */
function addMonths(date, months) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const daysInTarget = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, daysInTarget));
  return d;
}

/** Whole calendar days from `a` to `b` (start-of-day, avoids time-of-day drift). */
function dayDiff(a, b) {
  return Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86400000);
}

function floorIt(num, precision) {
  return Math.floor(num / precision) * precision;
}

/** MM/DD/YYYY, matching the reference 7YearCycles card. */
export function formatDate(date) {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${m}/${d}/${date.getFullYear()}`;
}

// ---------------------------------------------------------------------------
// Turning-point model
// ---------------------------------------------------------------------------

function formatPhaseTitle(sign, stage) {
  return `${sign} Phase:${stage}`;
}

function parsePhaseTitle(title) {
  const cleaned = String(title || "").replace(":", " ");
  const sign = ZODIAC_PHASES.find((name) => cleaned.startsWith(name)) || ZODIAC_PHASES[0];
  const stage = CHALLENGE_STAGES.find((name) => cleaned.includes(name)) || CHALLENGE_STAGES[0];
  return { sign, stage };
}

function getTurningPointType(index) {
  if (INITIATION_POINT_INDICES.has(index)) {
    return {
      label: "Initiation / Catalyst",
      className: "is-initiation",
      meaning: "A cardinal-phase spark that starts a three-phase growth process.",
    };
  }
  if (REORIENTATION_POINT_INDICES.has(index)) {
    return {
      label: "Reorientation / Crucible",
      className: "is-reorientation",
      meaning: "A fixed-phase midpoint that asks for internal revaluation.",
    };
  }
  return {
    label: "Standard Turning Point",
    className: "is-standard",
    meaning: "A turning point where the current challenge tends to culminate and resolve more easily.",
  };
}

/** 48 turning points: 12 Zodiac Phases × 4 Challenge Stages across 84 months. */
export function buildTurningPoints(cycleStart) {
  const points = [];
  for (let signIndex = 0; signIndex < ZODIAC_PHASES.length; signIndex++) {
    const phaseStart = addMonths(cycleStart, signIndex * 7);
    const nextPhaseStart = addMonths(cycleStart, (signIndex + 1) * 7);
    // Match the reference: each quarter step is rounded to whole days before
    // accumulating (a 53.5-day step becomes 54, so phase dates stay anchored).
    const quarterDays = Math.round(dayDiff(nextPhaseStart, phaseStart) / 4);

    for (let stageIndex = 0; stageIndex < CHALLENGE_STAGES.length; stageIndex++) {
      const index = signIndex * 4 + stageIndex;
      const date = addDays(phaseStart, quarterDays * stageIndex);
      points.push({
        index,
        sign: ZODIAC_PHASES[signIndex],
        stage: CHALLENGE_STAGES[stageIndex],
        title: formatPhaseTitle(ZODIAC_PHASES[signIndex], CHALLENGE_STAGES[stageIndex]),
        date,
        type: getTurningPointType(index),
      });
    }
  }
  return points;
}

function findNearestTurningPoint(turningPoints, referenceDate) {
  return turningPoints.reduce((nearest, point) => {
    const diffDays = dayDiff(referenceDate, point.date);
    if (!nearest || Math.abs(diffDays) < Math.abs(nearest.diffDays)) {
      return { ...point, diffDays };
    }
    return nearest;
  }, null);
}

/**
 * First turning point on or after the reference date. This advances the moment
 * a turning point passes, so the "Next Turning Point" box can always be marked
 * on a calendar. (`diffDays` is 0 on the day itself, negative in the future.)
 */
function findNextTurningPoint(turningPoints, referenceDate) {
  let next = null;
  for (const point of turningPoints) {
    const diffDays = dayDiff(referenceDate, point.date);
    if (diffDays <= 0 && (!next || diffDays > next.diffDays)) {
      next = { ...point, diffDays };
    }
  }
  return next;
}

/**
 * Turning point the wave graph — and the reading beneath it — should centre on.
 *
 * Before the crest (and on the day itself) the focus is the point being
 * approached. After the crest the focus stays with that same point through its
 * short resolution tail, and on day +8 it hands over to the next turning-point
 * period, with a fresh wave centred on that next date. Because the wave window
 * stays ±22 days, today then falls outside the new wave and no marker is drawn
 * until that next date comes within 22 days.
 */
function findActiveWaveTurningPoint(turningPoints, referenceDate) {
  const nearest = findNearestTurningPoint(turningPoints, referenceDate);
  const next = findNextTurningPoint(turningPoints, referenceDate);
  if (!nearest) return next;

  // diffDays > 0 means the point is already behind us (+ = after the date).
  if (nearest.diffDays <= 0) return nearest;
  if (nearest.diffDays < WAVE_FOCUS_AFTER_DAYS) return nearest;

  return next || nearest;
}

function findCurrentPhase(cycleStart, referenceDate) {
  for (let signIndex = ZODIAC_PHASES.length - 1; signIndex >= 0; signIndex--) {
    const phaseStart = addMonths(cycleStart, signIndex * 7);
    if (startOfDay(referenceDate) >= startOfDay(phaseStart)) {
      const phaseEnd = addMonths(cycleStart, (signIndex + 1) * 7);
      const quarterDays = dayDiff(phaseEnd, phaseStart) / 4;
      const daysIntoPhase = Math.max(0, dayDiff(referenceDate, phaseStart));
      const stageIndex = Math.min(3, Math.floor(daysIntoPhase / quarterDays));
      return {
        sign: ZODIAC_PHASES[signIndex],
        stage: CHALLENGE_STAGES[stageIndex],
        signIndex,
        stageIndex,
      };
    }
  }
  return { sign: ZODIAC_PHASES[0], stage: CHALLENGE_STAGES[0], signIndex: 0, stageIndex: 0 };
}

// ---------------------------------------------------------------------------
// Intensity / window classification
// ---------------------------------------------------------------------------

function isWithinInitiationWindow(nearest) {
  return Boolean(
    nearest &&
      nearest.type &&
      nearest.type.className === "is-initiation" &&
      Math.abs(nearest.diffDays) <= INITIATION_WINDOW_DAYS
  );
}

function isWithinReorientationWindow(nearest) {
  return Boolean(
    nearest &&
      nearest.type &&
      nearest.type.className === "is-reorientation" &&
      Math.abs(nearest.diffDays) <= REORIENTATION_WINDOW_DAYS
  );
}

/**
 * Classify the reference date relative to the nearest turning point.
 * Outer standard challenge window: -16 … +3 (cresting is the -3 … +3 peak inside it).
 * Special points (Initiation / Reorientation): ±31 days.
 */
export function classifyWindow(nearest) {
  if (!nearest || nearest.diffDays === undefined || nearest.diffDays === null) {
    return {
      band: "low",
      showPhaseImage: false,
      showHandDayCount: false,
      challengeLabel: "Low Challenge Period",
      intensity: {
        label: "Low Challenge Period",
        className: "is-low",
        guidance: LOW_CHALLENGE_GUIDANCE,
      },
    };
  }

  const d = nearest.diffDays;
  const inInitiation = isWithinInitiationWindow(nearest);
  const inReorientation = isWithinReorientationWindow(nearest);

  if (inInitiation || inReorientation) {
    return {
      band: inInitiation ? "initiation" : "reorientation",
      showPhaseImage: true,
      showHandDayCount: true,
      challengeLabel: nearest.stage,
      intensity: {
        label: inInitiation ? "Initiation Window" : "Reorientation Window",
        className: inInitiation ? "is-initiation" : "is-reorientation",
        guidance: inInitiation ? INITIATION_WINDOW_GUIDANCE : REORIENTATION_WINDOW_GUIDANCE,
      },
    };
  }

  const inStandardChallenge =
    d >= ACTIVE_CHALLENGE_START_DAYS && d <= ACTIVE_CHALLENGE_END_DAYS;
  const inCresting = d >= CRESTING_START_DAYS && d <= CRESTING_END_DAYS;

  if (inCresting) {
    return {
      band: "cresting",
      showPhaseImage: true,
      showHandDayCount: true,
      challengeLabel: nearest.stage,
      intensity: {
        label: "The Cresting Window",
        className: "is-peak",
        guidance:
          "The Active Challenges are cresting and should soon resolve/relax after the actual Turning Point is reached.",
      },
    };
  }

  if (inStandardChallenge) {
    return {
      band: "active",
      showPhaseImage: true,
      showHandDayCount: true,
      challengeLabel: nearest.stage,
      intensity: {
        label: "Active Challenge Window",
        className: "is-active",
        guidance:
          "You are inside the broader challenge/opportunity window surrounding a turning point (16 days before through 3 days after). Watch what is asking for attention.",
      },
    };
  }

  return {
    band: "low",
    showPhaseImage: false,
    showHandDayCount: false,
    challengeLabel: "Low Challenge Period",
    intensity: {
      label: "Low Challenge Period",
      className: "is-low",
      guidance: LOW_CHALLENGE_GUIDANCE,
    },
  };
}

export function describeDayDistance(diffDays) {
  if (diffDays === 0) return "today — exact turning point";
  if (diffDays > 0) return `${diffDays} day${diffDays === 1 ? "" : "s"} after turning point`;
  return `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"} before turning point`;
}

/** Forward-looking phrasing for the calendar "Next Turning Point" card. */
export function describeCountdown(diffDays) {
  if (diffDays === 0) return "today — mark your calendar";
  if (diffDays < 0) {
    const n = Math.abs(diffDays);
    return `in ${n} day${n === 1 ? "" : "s"}`;
  }
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Compute the full 7-Year Life Clock state.
 * @param {object} input
 * @param {Date|string} input.birthDate   Date of birth.
 * @param {Date|string} [input.referenceDate]  Date to read the clock at (defaults to now).
 * @param {string} [input.name]           Person's name for the card title.
 * @returns {object|null} clock state, or null if inputs are invalid.
 */
export function computeLifeClock({ birthDate, referenceDate, name } = {}) {
  const birth = birthDate ? startOfDay(new Date(birthDate)) : null;
  if (!birth || Number.isNaN(birth.getTime())) return null;
  const reference = referenceDate ? startOfDay(new Date(referenceDate)) : startOfDay(new Date());
  if (Number.isNaN(reference.getTime())) return null;

  const daysSinceBirth = dayDiff(reference, birth);
  const ageAtReference = daysSinceBirth / 365.25;

  // Cycle start = birth + whole 7-year blocks completed, expressed in months.
  const flooredYears = floorIt(ageAtReference, 7);
  const cycleStart = addMonths(birth, Math.round(flooredYears * 12));
  const cycleEnd = addMonths(cycleStart, 84);

  const turningPoints = buildTurningPoints(cycleStart);
  // Append the next cycle's opening turning point so the forward lookups never
  // run dry in the gap at the very end of the present 7-year cycle.
  const lookahead = turningPoints.concat([{ ...turningPoints[0], date: cycleEnd }]);

  const nearest = findNearestTurningPoint(turningPoints, reference);
  const next = findNextTurningPoint(lookahead, reference) || nearest;
  const activeWave =
    findActiveWaveTurningPoint(lookahead, reference) || nearest;
  const currentPhase = findCurrentPhase(cycleStart, reference);
  const windowInfo = classifyWindow(nearest);

  const cycleNumber = Math.floor(ageAtReference / 7) + 1;
  const cycleStartAge = Math.max(0, (cycleNumber - 1) * 7);
  const cycleEndAge = cycleStartAge + 7;

  // Low Challenge Period stands alone: naming a phase here would compete with
  // the present phase indicator shown elsewhere on the card.
  const guidanceTitle =
    windowInfo.band === "low"
      ? windowInfo.intensity.label
      : `${windowInfo.intensity.label}: ${nearest.sign} ${nearest.stage}`;

  return {
    name: (name || "").trim(),
    birthDate: birth,
    referenceDate: reference,
    cycleStart,
    cycleEnd,
    cycleNumber,
    cycleStartAge,
    cycleEndAge,
    ageAtReference,
    turningPoints,
    nearest,
    next,
    activeWave,
    currentPhase,
    window: windowInfo,
    guidanceTitle,
    guidanceText: windowInfo.intensity.guidance,
    /** Days from reference to the nearest turning point (− = before, + = after). */
    daysToNearest: nearest ? nearest.diffDays : null,
    /** Days until the upcoming turning point (− = future, 0 = today). */
    daysToNext: next ? next.diffDays : null,
    /** Days to the turning point the wave graph centres on (− = before, + = after). */
    daysToActiveWave: activeWave ? activeWave.diffDays : null,
  };
}



